import React from "react";
import ReactSelect from "react-select";
import { twMerge } from "tailwind-merge";

const Select = ({
  name,
  label,
  value,
  options = [],
  required,
  errors,
  errorCustomId,
  placeholder = "",
  border = "border-[var(--color-primary)]",
  padding = "p-[0.515rem]",
  disabled = false,
  isMulti = false,
  ...props
}) => {
  const valueContainerStyles = twMerge("gap-1 text-zinc-900", padding, isMulti && "py-[0.7rem]");
  const indicatorsContainerStyles = "p-1 gap-1";
  const indicatorSeparatorStyles = "bg-gray-300";
  const multiValueLabelStyles = "leading-5";
  const menuStyles =
    "p-1 mt-2 border border-gray-200 z-[1502!important] bg-white rounded-lg text-zinc-900";
  const multiValueStyles = "bg-gray-300 rounded-sm items-center py-1 pl-2 pr-1 gap-1.5";
  const clearIndicatorStyles = "text-gray-400 rounded-md hover:bg-red-50 hover:text-rose-800";
  const dropdownIndicatorStyles = "hover:bg-gray-100 text-gray-400 rounded-md hover:text-black";
  const noOptionsMessageStyles =
    "text-gray-900 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-xs";
  const multiValueRemoveStyles =
    "border border-gray-200 bg-white hover:bg-red-50 hover:text-rose-600 text-rose-500 hover:border-red-300 rounded-md";
  const optionStyles = {
    base: "hover:cursor-pointer px-3 py-2 rounded-sm",
    focus: "bg-gray-100 active:bg-gray-200",
    selected: "after:content-['✔'] after:ml-2 after:text-green-500 text-black",
  };
  return (
    <div className="w-full relative group">
      <ReactSelect
        inputId={name}
        unstyled
        isDisabled={disabled}
        placeholder={placeholder}
        options={options}
        isMulti={isMulti}
        value={value}
        styles={{
          input: (base) => ({
            ...base,
            "input:focus": {
              boxShadow: "none",
            },
          }),
          multiValueLabel: (base) => ({
            ...base,
            whiteSpace: "normal",
            overflow: "visible",
          }),
          control: (base) => ({
            ...base,
            transition: "none",
          }),
        }}
        classNames={{
          control: (state) => {
            return twMerge(
              "peer w-full outline-hidden hover:cursor-pointer border-[1.5px] rounded-md transition disabled:opacity-70 disabled:cursor-not-allowed",
              state.isFocused || value?.length > 0 || Object.keys(value || {}).length > 0
                ? border
                : errors?.[name] || errorCustomId
                  ? "border-rose-500"
                  : "border-gray-300",
              disabled ? "bg-gray-200" : "bg-white",
            );
          },
          menu: () => menuStyles,
          multiValue: () => multiValueStyles,
          clearIndicator: () => clearIndicatorStyles,
          valueContainer: () => valueContainerStyles,
          multiValueLabel: () => multiValueLabelStyles,
          multiValueRemove: () => multiValueRemoveStyles,
          dropdownIndicator: () => dropdownIndicatorStyles,
          indicatorSeparator: () => indicatorSeparatorStyles,
          indicatorsContainer: () => indicatorsContainerStyles,
          option: ({ isFocused, isSelected }) =>
            twMerge(
              isFocused && optionStyles.focus,
              isSelected && optionStyles.selected,
              optionStyles.base,
            ),
          noOptionsMessage: () => noOptionsMessageStyles,
        }}
        {...props}
      />
      <label
        htmlFor={name}
        className={twMerge(
          `absolute pointer-events-none flex items-center gap-1 justify-center px-1 cursor-text origin-left left-3 group-focus-within:transform group-focus-within:duration-150 group-focus-within:top-[-0.515rem] group-focus-within:left-4.5 group-focus-within:text-(--color-primary) group-focus-within:scale-90 text-sm
        `,
          errors?.[name] || errorCustomId ? "text-rose-500" : "text-zinc-700",
          isMulti && value?.length > 0
            ? `duration-150 transform left-3 top-[-0.515rem] text-(--color-primary)`
            : value?.length > 0 || Object.keys(value || {}).length > 0
              ? `duration-150 transform left-3 top-[-0.615rem] text-(--color-primary)`
              : "top-3 duration-150 transform",
          disabled
            ? value?.length > 0 || Object.keys(value || {}).length > 0
              ? "bg-linear-to-b from-white to-gray-200"
              : "bg-transparent"
            : "bg-white",
        )}
      >
        {label}
        {required && <span className="text-rose-500">*</span>}
      </label>
      {(errors?.[name] || errorCustomId) && (
        <p className="text-rose-500 text-sm mt-2">
          {errors?.[name]?.value
            ? errors?.[name]?.value?.message || errorCustomId?.message || ""
            : errors?.[name]?.message || errorCustomId?.message || ""}
        </p>
      )}
    </div>
  );
};

export default Select;
